import React from "react";
import { Paper, Table, TableHead, TableBody, Hidden, Grid } from "@material-ui/core";
import { Values } from "@src/Tools/Values";
import { AuditLog, AuditLogAction } from "@src/Interfaces/Entity/AuditLog";
import {
  STHeaderTableRow,
  STBodyTableRow,
  STHeaderTableCell,
  STBodyTableCell,
  STEmptyDataTableCell,
  STReviewCheckbox,
  STReviewByText,
  STSelectCheckbox,
} from "./styles";
import moment from "moment-timezone";
import { ISessionTokenLocation } from "@src/Stores/Auth/types";

interface IProps {
  currentLocation: ISessionTokenLocation;
  currentUserId: string;
  auditLogData?: AuditLog[];
  handleGetSeverity(severity: number): string;
  handleGetType(type: number): string;
  handleGetColor(severity: number): object;
  handleSelectedCheckboxChange?: (auditLogRecordId: string) => void;
  handleSelectedAllCheckboxChange?: () => void;
  handleGetLocationName?: (locationId: string) => string;
  actionToString(action: AuditLogAction): string;
  handleGetReviewBy(reviewedBy: string): string;
  isPendingReviewTable: boolean;
  isAllRecordsSelected?: boolean;
}

const AuditLogTable = (props: IProps) => {
  const {
    currentLocation,
    currentUserId,
    auditLogData,
    handleGetColor,
    handleGetType,
    handleGetSeverity,
    actionToString,
    isPendingReviewTable,
    handleSelectedCheckboxChange,
    isAllRecordsSelected,
    handleSelectedAllCheckboxChange,
    handleGetLocationName,
    handleGetReviewBy,
  } = props;

  return (
    <Paper elevation={2}>
      <Table>
        <colgroup>
          {isPendingReviewTable && <col width="3%" />}
          <col width="2%" />
          <col width="12%" />
          <col width="12%" />
          <col width="12%" />
          <col width="9%" />
          <col width="35%" />
          {!isPendingReviewTable && <col width="25%" />}
        </colgroup>
        <TableHead>
          <STHeaderTableRow>
            {isPendingReviewTable && (
              <STHeaderTableCell align="center" padding="none">
                <STSelectCheckbox
                  checked={Boolean(isAllRecordsSelected)}
                  value="checked"
                  color="default"
                  onChange={handleSelectedAllCheckboxChange}
                />
              </STHeaderTableCell>
            )}
            <STHeaderTableCell />
            <STHeaderTableCell align="center" padding="none">
              {Values.getLocalizationValue("date")}
            </STHeaderTableCell>
            <STHeaderTableCell align="center" padding="none">
              {Values.getLocalizationValue("location")}
            </STHeaderTableCell>
            <STHeaderTableCell align="center" padding="none">
              {Values.getLocalizationValue("type")}
            </STHeaderTableCell>
            <STHeaderTableCell align="center" padding="none">
              {Values.getLocalizationValue("severity")}
            </STHeaderTableCell>
            <STHeaderTableCell align="center" padding="none">
              {Values.getLocalizationValue("action")}
            </STHeaderTableCell>
            {!isPendingReviewTable && (
              <STHeaderTableCell align="center" padding="none">
                {Values.getLocalizationValue("reviewed")}
              </STHeaderTableCell>
            )}
          </STHeaderTableRow>
        </TableHead>
        <TableBody>
          {(auditLogData || []).map((auditLogRow) => (
            <STBodyTableRow key={auditLogRow.id}>
              {isPendingReviewTable && (
                <STBodyTableCell align="center" padding="none">
                  <STSelectCheckbox
                    checked={Boolean(auditLogRow.checked)}
                    value="checked"
                    color="default"
                    disabled={currentUserId === auditLogRow.createdBy}
                    onChange={() => handleSelectedCheckboxChange(auditLogRow.id)}
                  />
                </STBodyTableCell>
              )}
              <STBodyTableCell
                id="severity-color-column"
                style={handleGetColor(auditLogRow.severity)}
                align="center"
                padding="none"
              />
              <STBodyTableCell align="center" size="small">
                {currentLocation.timeZone
                  ? moment(auditLogRow.time).utc().tz(currentLocation.timeZone).format("YYYY-MM-DD HH:mm")
                  : moment(auditLogRow.time).utc().format("YYYY-MM-DD HH:mm")}
              </STBodyTableCell>
              <STBodyTableCell align="center" size="small">
                {handleGetLocationName(auditLogRow.locationId)}
              </STBodyTableCell>
              <STBodyTableCell align="center" size="small">
                {handleGetType(auditLogRow.type)}
              </STBodyTableCell>
              <STBodyTableCell align="center" size="small">
                {handleGetSeverity(auditLogRow.severity)}
              </STBodyTableCell>
              <STBodyTableCell align="left" size="small">
                {actionToString(auditLogRow.action)}
              </STBodyTableCell>
              {!isPendingReviewTable && (
                <STBodyTableCell align="left" size="small">
                  {auditLogRow.reviewedBy && (
                    <Grid container alignItems="center">
                      <Grid item xs={1}>
                        <STReviewCheckbox checked value="reviewed" color="default" />
                      </Grid>
                      <Grid item xs={11}>
                        <STReviewByText>{handleGetReviewBy(auditLogRow.reviewedBy)}</STReviewByText>
                      </Grid>
                    </Grid>
                  )}
                </STBodyTableCell>
              )}
            </STBodyTableRow>
          ))}
          <Hidden xsUp={!auditLogData || auditLogData.length !== 0}>
            <STBodyTableRow>
              <STEmptyDataTableCell colSpan={6}>{Values.getLocalizationValue("noDataFound")}</STEmptyDataTableCell>
            </STBodyTableRow>
          </Hidden>
        </TableBody>
      </Table>
    </Paper>
  );
};

export default AuditLogTable;
